// src/components/Leadership/PeopleChange.js
import React from 'react';
import { Users, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import '../../styles/Leadership/PeopleChange.css';

const PeopleChange = () => {
  const changeManagementAreas = [
    {
      icon: <Users className="change-icon" />,
      emoji: "👥",
      title: "Staff Turnover Management",
      description: "Frameworks to minimize disruption and maintain continuity.",
      strategies: [
        "Knowledge transfer protocols",
        "Documentation as insurance",
        "Cross-training initiatives",
        "Succession planning"
      ],
      impact: "Reduce onboarding time by 60%",
      color: "from-blue-600 to-cyan-600"
    },
    {
      icon: <RefreshCw className="change-icon" />,
      emoji: "🔄",
      title: "Change Management",
      description: "Structured processes to guide teams through transformation with minimal risk.",
      strategies: [
        "Stakeholder communication plans",
        "Phased rollout strategies",
        "Training and enablement",
        "Feedback loops"
      ],
      impact: "85% adoption rate within 90 days",
      color: "from-purple-600 to-pink-600"
    },
    {
      icon: <AlertTriangle className="change-icon" />,
      emoji: "⚡",
      title: "Incident Management",
      description: "Rapid response protocols to protect operations and reputation.",
      strategies: [
        "24/7 incident response team",
        "Automated alerting systems",
        "Root cause analysis",
        "Post-mortem reviews"
      ],
      impact: "< 15min mean time to detection",
      color: "from-red-600 to-orange-600"
    }
  ];

  const continuityBenefits = [
    {
      icon: <CheckCircle className="benefit-icon" />,
      title: "Institutional Knowledge Preserved",
      description: "No critical information walks out the door"
    },
    {
      icon: <CheckCircle className="benefit-icon" />,
      title: "Minimal Productivity Loss",
      description: "Teams stay operational during transitions"
    },
    {
      icon: <CheckCircle className="benefit-icon" />,
      title: "Stakeholder Confidence",
      description: "Leadership sees controlled, predictable change"
    }
  ];

  return (
    <section className="people-change">
      <div className="people-change-container">
        <div className="section-header-center">
          <span className="section-badge">People & Change</span>
          <h2 className="section-title">
            Managing the Human Side of Technology
          </h2>
          <p className="section-description">
            Technology projects fail not because of code—but because of people. 
            We manage transitions, turnover, and incidents with frameworks that protect your operations.
          </p>
        </div>

        <div className="change-management-grid">
          {changeManagementAreas.map((area, index) => (
            <div key={index} className="change-card">
              <div className={`change-card-header bg-gradient-to-br ${area.color}`}>
                <div className="change-emoji">{area.emoji}</div>
                <h3 className="change-card-title">{area.title}</h3>
                <p className="change-card-description">{area.description}</p>
              </div>

              <div className="change-card-content">
                <div className="strategies-section">
                  <h4 className="strategies-title">Key Strategies:</h4>
                  <div className="strategies-list">
                    {area.strategies.map((strategy, idx) => (
                      <div key={idx} className="strategy-item">
                        <div className="strategy-bullet"></div>
                        <span>{strategy}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="impact-badge">
                  <span className="impact-label">Measurable Impact:</span>
                  <span className="impact-value">{area.impact}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Continuity Benefits */}
        <div className="continuity-section">
          <h3 className="continuity-title">Why Continuity Matters</h3>
          <div className="continuity-grid">
            {continuityBenefits.map((benefit, index) => (
              <div key={index} className="continuity-card">
                <div className="continuity-icon-wrapper">
                  {benefit.icon}
                </div>
                <h4 className="continuity-card-title">{benefit.title}</h4>
                <p className="continuity-card-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Mitigation Statement */}
        <div className="risk-mitigation-box">
          <div className="risk-icon-section">
            <AlertTriangle className="risk-icon" />
          </div>
          <div className="risk-content">
            <h3 className="risk-title">Protecting Your Investment</h3>
            <p className="risk-text">
              Every technology initiative carries people risk. Staff leave. Requirements change. 
              Incidents happen. The difference between success and failure is having frameworks 
              in place <em>before</em> disruption occurs. We build resilience into your organization, 
              not just your codebase.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PeopleChange;